// ============================================================================
// JOURNEYS — universal automation engine (B3 řez 2)
// ============================================================================
//
// Authoritative TS types for the journey engine. The db schema (`packages/db/src/schema/
// journeys.ts`) mirrors these as JSON column shapes (the db package has no dependency on
// shared, so the shapes are re-declared there — keep the two in sync). Zod validation lives
// in `packages/shared/src/schemas/journeys.ts`.

export const JOURNEY_SUBJECT_TYPES = [
  "order",
  "lead",
  "customer",
  "service_booking",
  "trip_date",
  "invoice",
  "review",
  "complaint",
  "participant",
] as const;
export type JourneySubjectType = (typeof JOURNEY_SUBJECT_TYPES)[number];

export const JOURNEY_SUBJECT_TYPE_LABELS: Record<JourneySubjectType, string> = {
  order: "Order",
  lead: "Inquiry",
  customer: "Customer",
  service_booking: "Service booking",
  trip_date: "Departure date",
  invoice: "Invoice",
  review: "Review",
  complaint: "Complaint",
  participant: "Participant",
};

// How a marketing-class send is gated. transactional/ops MISS the marketing gate
// (suppression + frequency cap); marketing goes THROUGH it.
export const JOURNEY_SEND_CLASSES = ["marketing", "transactional", "ops"] as const;
export type JourneySendClass = (typeof JOURNEY_SEND_CLASSES)[number];

export const JOURNEY_SEND_CLASS_LABELS: Record<JourneySendClass, string> = {
  marketing: "Marketing (gated)",
  transactional: "Transactional",
  ops: "Operational",
};

// once = enroll at most once ever; once_per_completion = re-enroll only when no active
// enrollment of the pair; always = enroll on every qualifying event.
export const JOURNEY_REENROLLMENTS = ["once", "once_per_completion", "always"] as const;
export type JourneyReenrollment = (typeof JOURNEY_REENROLLMENTS)[number];

export const JOURNEY_REENROLLMENT_LABELS: Record<JourneyReenrollment, string> = {
  once: "Once per subject",
  once_per_completion: "Once per completion",
  always: "Every time",
};

export const ENROLLMENT_STATUSES = ["active", "completed", "cancelled", "failed"] as const;
export type EnrollmentStatus = (typeof ENROLLMENT_STATUSES)[number];

export const STEP_RUN_STATUSES = ["completed", "skipped", "failed"] as const;
export type StepRunStatus = (typeof STEP_RUN_STATUSES)[number];

// ── Triggers ────────────────────────────────────────────────────────────────────────────

export const JOURNEY_TRIGGER_TYPES = [
  "status_change",
  "field_change",
  "time_offset",
  "event",
  "inactivity",
  "recurring",
  "manual",
] as const;
export type JourneyTriggerType = (typeof JOURNEY_TRIGGER_TYPES)[number];

export const JOURNEY_TRIGGER_TYPE_LABELS: Record<JourneyTriggerType, string> = {
  status_change: "Status change",
  field_change: "Field change / threshold",
  time_offset: "Days from a date",
  event: "Event",
  inactivity: "Inactivity",
  recurring: "Recurring",
  manual: "Manual only",
};

export type JourneyTrigger =
  | { type: "status_change"; from?: string[]; to: string[] }
  | { type: "field_change"; field: string; toValue?: string | number; gte?: number; lte?: number }
  | {
      type: "time_offset";
      dateField: string;
      daysOffset: number;
      timeOfDay?: string;
      onlyIfStatus?: string[];
    }
  | { type: "event"; eventType: string }
  | { type: "inactivity"; sinceField: string; days: number; onlyIfStatus?: string[] }
  | { type: "recurring"; everyDays?: number; cron?: string }
  | { type: "manual" };

// ── Conditions ──────────────────────────────────────────────────────────────────────────

export const CONDITION_OPS = [
  "eq",
  "neq",
  "gt",
  "lt",
  "gte",
  "lte",
  "contains",
  "in",
  "is_set",
  "is_empty",
] as const;
export type ConditionOp = (typeof CONDITION_OPS)[number];

export const CONDITION_OP_LABELS: Record<ConditionOp, string> = {
  eq: "is",
  neq: "is not",
  gt: ">",
  lt: "<",
  gte: "≥",
  lte: "≤",
  contains: "contains",
  in: "is one of",
  is_set: "is set",
  is_empty: "is empty",
};

export interface ConditionLeaf {
  field: string;
  op: ConditionOp;
  value?: string | number | boolean | Array<string | number>;
}

export interface ConditionGroup {
  all?: Array<ConditionLeaf | ConditionGroup>;
  any?: Array<ConditionLeaf | ConditionGroup>;
}

// ── Actions ─────────────────────────────────────────────────────────────────────────────

export const JOURNEY_ACTION_TYPES = [
  "send_email",
  "send_sms",
  "send_whatsapp",
  "create_task",
  "assign",
  "notify_user",
  "add_to_segment",
  "remove_from_segment",
  "generate_document",
  "webhook",
  "agent_action",
] as const;
export type JourneyActionType = (typeof JOURNEY_ACTION_TYPES)[number];

export const JOURNEY_ACTION_TYPE_LABELS: Record<JourneyActionType, string> = {
  send_email: "Send email",
  send_sms: "Send SMS",
  send_whatsapp: "Send WhatsApp",
  create_task: "Create task",
  assign: "Assign owner",
  notify_user: "Notify user",
  add_to_segment: "Add to segment",
  remove_from_segment: "Remove from segment",
  generate_document: "Generate document",
  webhook: "Webhook",
  agent_action: "Ask the agent (raise proposal)",
};

export type EmailRecipient = "customer" | "assignee" | "admin" | "email_field";
export type PhoneRecipient = "customer" | "assignee" | "phone_field";

export type JourneyAction =
  | {
      type: "send_email";
      recipient: EmailRecipient;
      emailField?: string;
      templateSlug?: string;
      subject?: string;
      body?: string;
    }
  | {
      type: "send_sms";
      recipient: PhoneRecipient;
      phoneField?: string;
      templateSlug?: string;
      body?: string;
    }
  | {
      type: "send_whatsapp";
      recipient: PhoneRecipient;
      phoneField?: string;
      templateSlug?: string;
      body?: string;
    }
  | {
      type: "create_task";
      kind?: "callback" | "manual" | "supplier_followup";
      title: string;
      description?: string;
      dueInDays?: number;
      assignTo?: "subject_assignee" | "user";
      userId?: string;
    }
  | { type: "assign"; mode: "round_robin" | "user"; userId?: string }
  | { type: "notify_user"; target: "subject_assignee" | "user"; userId?: string; message: string }
  | { type: "add_to_segment"; segmentId: string }
  | { type: "remove_from_segment"; segmentId: string }
  | { type: "generate_document"; docType: "contract" | "itinerary" | "voucher" | "package" }
  | { type: "webhook"; url: string; method?: "POST" | "GET"; bodyTemplate?: string }
  // Hand the subject to the agent "MAKEJ" with a written instruction. RAISE-ONLY: the agent
  // reads the subject and raises a proposal a human approves in-app (it never executes). The
  // instruction is `{{vars}}`-templated. `allowMoney` is the safety ceiling — false (default)
  // lets the agent raise read/write proposals (tasks, notes, drafts); true also allows money
  // proposals (invoice/cancellation/date-change/send), which still need the usual approval.
  | { type: "agent_action"; instruction: string; allowMoney?: boolean };
// DEFERRED: set_field (whitelisted field write) — YAGNI until a concrete need appears.

export interface JourneyStep {
  delay?: { days?: number; hours?: number; atTimeOfDay?: string };
  conditions?: ConditionGroup;
  ifConditionsFail?: "skip_step" | "exit_journey";
  actions: JourneyAction[];
}

// ── Journey events (the event bus the engine listens on) ────────────────────────────────

export interface JourneyEvent {
  type: string;
  subjectType: JourneySubjectType;
  subjectId: string;
  oldStatus?: string;
  newStatus?: string;
  payload?: Record<string, unknown>;
}

// ── Field registry — drives the trigger/condition pickers per subject ───────────────────

export type FieldType = "string" | "number" | "date" | "enum" | "boolean";

export interface FieldSpec {
  key: string;
  label: string;
  type: FieldType;
  options?: Array<{ value: string; label: string }>;
  /** A date field offsettable by `time_offset` triggers. */
  date?: boolean;
  /** The subject's primary status field (drives `status_change` / `onlyIfStatus`). */
  status?: boolean;
}
